'use client';

import React, { FC, useEffect, useMemo, useRef } from 'react';
import Section from '../../components/Section';
import ProductInfo from './ProductInfo/ProductInfo';
import ProductSlide from './ProductSlide/ProductSlide';
import AlsoLikes from './AlsoLikes/AlsoLikes';
import { ProductInfoStyled, SectionContainer } from './styles.ts';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { reducerTypes, sagaTypes } from '../../store/Product/types';
import { usePathname, useSearchParams } from 'next/navigation';
import { FilterOptionEnum } from '../Catalog/types';
import { setSelectedCurrencyAction } from '../../store/SelectedCurrency/action';
import styles from './styles.module.css';
import { IProduct } from '../../interfaces/product';
import { Container } from '@mui/material';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';

interface ProductProps {
  ssrProduct?: IProduct;
  ssrAlsoLikedProducts?: IProduct[];
}

const Product: FC<ProductProps> = ({ ssrProduct, ssrAlsoLikedProducts }) => {
  const ssrInitialized = useRef(false);

  if (ssrInitialized.current === false && ssrProduct) {
    ssrInitialized.current = true;
  }
  const dispatch = useAppDispatch();

  const { selectedCurrency } = useAppSelector((st) => st.currency);
  const locale = useAppSelector((st) => st.locale);
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const breadcrumbs = useMemo(() => {
    const list = [];

    list.push({
      title: locale['heromock_all_watches_title'],
      href: '/all-watches',
    });

    if (ssrProduct?.model?.brand?.name) {
      list.push({
        title: ssrProduct.model.brand.name,
        href: `/all-watches/${ssrProduct.model.brand.urlSlug || ssrProduct.model.brand.name.toLowerCase()}`,
      });
    }
    if (ssrProduct?.model?.name) {
      list.push({
        title: ssrProduct.model.name,
        href: ssrProduct.model.brand?.name
          ? `/all-watches/${ssrProduct.model.brand.urlSlug || ssrProduct.model.brand.name.toLowerCase()}?modelname=${
              ssrProduct.model.urlSlug || ssrProduct.model.name.toLowerCase()
            }`
          : `/all-watches`,
      });
    }
    if (ssrProduct?.name) {
      list.push({
        title: ssrProduct.name,
      });
    }
    return list;
  }, [locale, ssrProduct]);

  useEffect(() => {
    const params = searchParams;
    const curr = params.get('curr');

    window.scrollTo({ top: 0, behavior: 'auto' });

    if (!ssrProduct) {
      const dealerProductCode = pathName?.substring(pathName.lastIndexOf('/') + 1)?.replaceAll('-', '/');
      dispatch({ type: reducerTypes.IS_LOADING, payload: !ssrInitialized.current });
      dispatch({
        type: sagaTypes.GET_PRODUCT,
        payload: `/products/1/0?filter[column]=DealerProductCode&filter[value]=${dealerProductCode}&filter[option]=${
          FilterOptionEnum.IsEqualTo
        }&curr=${curr || selectedCurrency}`,
      });
    }

    if (curr) {
      dispatch(setSelectedCurrencyAction(curr));
    }
  }, [dispatch, pathName, searchParams, selectedCurrency, ssrProduct]);

  return (
    <div>
      <Container maxWidth='lg1286'>
        <Breadcrumbs customBreadcrumbs={breadcrumbs} />
      </Container>

      <SectionContainer>
        <Section classContainer={styles.product} className={styles.productWrapper}>
          <ProductInfoStyled>
            <ProductSlide ssrProduct={ssrProduct} />
            <ProductInfo ssrProduct={ssrProduct} />
          </ProductInfoStyled>
        </Section>
        <AlsoLikes ssrProduct={ssrProduct} ssrAlsoLikedProducts={ssrAlsoLikedProducts} />
      </SectionContainer>
    </div>
  );
};

export default Product;
